import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { ApiError } from './api-client';

/** Merge Tailwind classes, with later classes winning conflicts. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format INR with Indian digit grouping, e.g. ₹24,78,000. */
export function formatCurrency(amount: number, options?: { showDecimals?: boolean }): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: options?.showDecimals ? 2 : 0,
    maximumFractionDigits: options?.showDecimals ? 2 : 0,
  }).format(amount);
}

/**
 * Abbreviate large figures for dashboard tiles using Indian units, so
 * ₹24,78,000 becomes "₹24.8L" and 1,20,00,000 becomes "₹1.2Cr".
 */
export function formatCompactCurrency(amount: number): string {
  if (Math.abs(amount) >= 10_000_000) {
    return `₹${(amount / 10_000_000).toFixed(1)}Cr`;
  }
  if (Math.abs(amount) >= 100_000) {
    return `₹${(amount / 100_000).toFixed(1)}L`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `₹${(amount / 1_000).toFixed(1)}K`;
  }
  return formatCurrency(amount);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

/** Signed percentage for period-over-period deltas, e.g. "+18.2%". */
export function formatPercentChange(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/**
 * Server field errors, keyed by field name.
 *
 * The API returns `errors: [{ field, message }]` alongside every validation
 * failure, so a form can put the message under the input that caused it rather
 * than showing one generic banner.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) {
    return {};
  }

  return error.fieldErrors.reduce<Record<string, string>>((accumulator, entry) => {
    // A nested path like `priceTiers[0].minQuantity` is keyed as sent; the tier
    // editor looks up exactly that string.
    accumulator[entry.field] ??= entry.message;
    return accumulator;
  }, {});
}

/** Human-readable text for any thrown value. */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export function formatDateTime(value: string | Date): string {
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(typeof value === 'string' ? new Date(value) : value);
}
